import type React from 'react';
import { Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { asDefinition, asTerm } from './productDetailTypography';

type ProductDetailFieldProps = {
    label: string;
    /** Names the value's node, `product-detail-field-<slot>`, where one is needed. */
    slot?: string;
    /** A figure: its digits line up with the figures around it. */
    isFigure?: boolean;
    /** The value, or its placeholder while the product loads. */
    children: React.ReactNode;
};

/**
 * One label and value on the details page: the summary card's (bos-app#139)
 * and a variant's figures. The label is static, so it shows while the product
 * loads; only the value waits (rulebook §12).
 *
 * Typography has no prop for tabular figures, so a figure takes
 * `tabular-nums` through `className`; `.agents/frontend.md` registers the gap (§6).
 */
export const ProductDetailField: React.FC<ProductDetailFieldProps> = ({
    label,
    slot,
    isFigure = false,
    children,
}) => (
    <div className="flex flex-col gap-1">
        <Typography color="muted" render={asTerm} type="body-xs">
            {label}
        </Typography>
        <Typography
            className={isFigure ? 'tabular-nums' : undefined}
            render={asDefinition}
            type="body-sm"
            weight="medium"
            {...(slot === undefined
                ? {}
                : getNodeTestIdProp(`product-detail-field-${slot}`))}
        >
            {children}
        </Typography>
    </div>
);
